/**
 * Operation Registry — maps operation names to handler modules.
 *
 * Each handler module exports:
 *   meta: { name, category, inputs, outputs }
 *   execute(step, ctx): async function
 *
 * Operations are registered at import time. The registry is a simple Map
 * that the pipeline executor queries by op name.
 *
 * Phase 1: navigate, terrain
 * Phase 2+: boundaries, choropleth, bivariate, pois, duckdb, cities, indicators, etc.
 */

class OperationRegistry {
  constructor() {
    this._ops = new Map()
  }

  /**
   * Register an operation handler.
   * @param {string} name — operation name (e.g., 'navigate', 'render_choropleth')
   * @param {{ meta: object, execute: function }} handler — the operation module
   */
  register(name, handler) {
    if (this._ops.has(name)) {
      console.warn(`[OperationRegistry] Overwriting existing op: ${name}`)
    }
    if (typeof handler.execute !== 'function') {
      throw new Error(`[OperationRegistry] Handler for "${name}" must export an execute() function`)
    }
    this._ops.set(name, handler)
    console.log(`[OperationRegistry] Registered: ${name} (${handler.meta?.category || 'unknown'})`)
  }

  /**
   * Get a registered operation handler by name.
   * @param {string} name
   * @returns {{ meta: object, execute: function } | undefined}
   */
  get(name) {
    return this._ops.get(name)
  }

  /**
   * Check if an operation is registered.
   * @param {string} name
   * @returns {boolean}
   */
  has(name) {
    return this._ops.has(name)
  }

  /**
   * List all registered operation names.
   * @returns {string[]}
   */
  list() {
    return Array.from(this._ops.keys())
  }

  /**
   * List operations by category.
   * @param {string} category — 'data', 'transform', 'render', 'camera', 'output'
   * @returns {string[]}
   */
  listByCategory(category) {
    return Array.from(this._ops.entries())
      .filter(([, handler]) => handler.meta?.category === category)
      .map(([name]) => name)
  }
}

// Singleton registry instance
export const registry = new OperationRegistry()

/**
 * Import a batch of op modules fault-tolerantly. A single chunk that fails to
 * load — a transient CDN/edge hiccup (e.g. Cloudflare Pages briefly serving the
 * SPA-fallback HTML for a hashed /assets chunk in the post-deploy window), or
 * flaky school wifi — must NOT take down the whole batch. Returns an array in
 * the same order as `Promise.all` would, but with `null` in place of any module
 * that failed, so the ones that loaded still register. (CL-115: previously a
 * single rejected import rejected the whole Promise.all and left EVERY op in
 * that batch — up to ~20 — unregistered, so all queries failed with "unknown
 * operation".)
 */
export async function importSettled(promises) {
  const results = await Promise.allSettled(promises)
  return results.map((r) => {
    if (r.status === 'fulfilled') return r.value
    console.warn(
      '[OperationRegistry] an op module failed to load; it will be unavailable until reload:',
      r.reason?.message || r.reason
    )
    return null
  })
}

/** Register an op only if its module actually loaded (importSettled → null on failure). */
function reg(name, mod) {
  if (mod) registry.register(name, mod)
}

/**
 * Initialize the registry with all available operation modules.
 * Called once at app startup.
 *
 * Phase 1: navigate + terrain
 * Phase 2: All core handlers extracted from query-router.js
 * Phase 3+: Composition operations (normalize, aggregate, rank, etc.)
 */
export async function initRegistry() {
  // Phase 1 operations (camera)
  const [navigate, terrain, projection, compass] = await Promise.all([
    import('./ops/navigate.js'),
    import('./ops/terrain.js'),
    import('./ops/projection.js'),
    import('./ops/compass.js'),
  ])
  reg('navigate', navigate)
  reg('terrain', terrain)
  reg('projection', projection)
  reg('compass', compass)

  // Phase 2 operations — core handler extractions
  const [
    show,
    choropleth,
    filter,
    compare,
    unknown,
    overtureLayer,
    rivers,
    choroplethStyle,
    choroplethScale,
    duckdbQuery,
    forcedPoi,
    boundaries,
    poiInAdmin,
  ] = await Promise.all([
    import('./ops/show.js'),
    import('./ops/choropleth.js'),
    import('./ops/filter.js'),
    import('./ops/compare.js'),
    import('./ops/unknown.js'),
    import('./ops/overture-layer.js'),
    import('./ops/rivers.js'),
    import('./ops/choropleth-style.js'),
    import('./ops/choropleth-scale.js'),
    import('./ops/duckdb-query.js'),
    import('./ops/forced-poi.js'),
    import('./ops/boundaries.js'),
    import('./ops/poi-in-admin.js'),
  ])

  reg('show', show)
  reg('choropleth', choropleth)
  reg('analyze', choropleth) // analyze is alias for choropleth
  reg('filter', filter)
  reg('compare', compare)
  reg('unknown', unknown)
  reg('overture_layer', overtureLayer)
  reg('rivers', rivers)
  reg('choropleth_style', choroplethStyle)
  reg('choropleth_scale', choroplethScale)
  reg('duckdb_query', duckdbQuery)
  reg('forced_poi', forcedPoi)
  reg('boundaries', boundaries)
  reg('poi_in_admin', poiInAdmin)

  // Layer control — pedagogical overlay stacking (CL-165, roadmap H3.2):
  // hide/show/toggle/opacity/remove/list named stack entries.
  const [layerControl] = await importSettled([import('./ops/layer-control.js')])
  reg('layer_control', layerControl)

  // Water bodies — curated GeoJSON oceans / intermittent / salt flats
  const [waterLayer] = await importSettled([import('./ops/water_layer.js')])
  reg('water_layer', waterLayer)

  // Coordinate grid (graticule) — Grade-5 lat/long reference frame (CL-170)
  const [graticule] = await importSettled([import('./ops/graticule.js')])
  reg('graticule', graticule)

  // H3 density operation — hexagonal POI aggregation
  const [h3Density] = await importSettled([import('./ops/h3-density.js')])
  reg('h3_density', h3Density)

  // Routing operation — ORS directions, isochrones, matrix
  const [routing] = await importSettled([import('./ops/routing.js')])
  reg('routing', routing)

  // Geocode operation — HERE Geocoding API, drops a pin at the resolved address
  const [geocode] = await importSettled([import('./ops/geocode.js')])
  reg('geocode', geocode)

  // Route operation — HERE Routing API, point-to-point directions with polyline
  const [route] = await importSettled([import('./ops/route.js')])
  reg('route', route)

  // Climate zones operation — categorical polygon layer
  const [climateZones] = await importSettled([import('./ops/climate-zones.js')])
  reg('climate_zones', climateZones)

  // Freedom House Index — categorical 3-class polygon layer
  const [freedomHouse] = await importSettled([import('./ops/freedom-house.js')])
  reg('freedom_house', freedomHouse)

  // Fragile States Index — 7-class YlOrRd gradient polygon layer
  const [fragileStates] = await importSettled([import('./ops/fragile-states.js')])
  reg('fragile_states', fragileStates)

  // FAOSTAT Agriculture — multi-indicator gradient polygon layer
  const [faostat] = await importSettled([import('./ops/faostat.js')])
  reg('faostat', faostat)

  // USDA FAS — Production, Supply & Distribution + Export Sales (21 commodities)
  const [usdaFas] = await importSettled([import('./ops/usda-fas.js')])
  reg('usda_fas', usdaFas)

  // Global Food Security Index (EIU / Economist Impact) — 113 countries, 5 indicators
  const [foodSecurity] = await importSettled([import('./ops/food-security.js')])
  reg('food_security', foodSecurity)

  // UN Treaty Database — 27 treaties, binary + gradient choropleths
  const [unTreaties] = await importSettled([import('./ops/un-treaties.js')])
  reg('un_treaties', unTreaties)

  // UNESCO World Heritage Sites — point scatter layer (MapLibre native circles)
  const [heritageSites] = await importSettled([import('./ops/heritage-sites.js')])
  reg('heritage_sites', heritageSites)

  // World Languages (Glottolog) — 8000+ language locations (MapLibre native circles)
  const [languages] = await importSettled([import('./ops/languages.js')])
  reg('languages', languages)

  // World Religions & Ethnicity (CIA Factbook) — categorical choropleth on countries
  const [religions] = await importSettled([import('./ops/religions.js')])
  reg('religions', religions)

  // WHO Global Health Observatory — gradient choropleth on countries
  const [whoHealth] = await importSettled([import('./ops/who-health.js')])
  reg('who_health', whoHealth)

  // FAO Aquastat — Water Resources choropleth on countries
  const [aquastat] = await importSettled([import('./ops/aquastat.js')])
  reg('aquastat', aquastat)

  // WTO Trade Statistics — trade choropleth on countries
  const [wtoTrade] = await importSettled([import('./ops/wto-trade.js')])
  reg('wto_trade', wtoTrade)

  // Natural Earth Physical Features — mountains, deserts, glaciers, reefs, lakes
  const [physicalFeatures] = await importSettled([import('./ops/physical-features.js')])
  reg('physical_feature', physicalFeatures)

  // Smithsonian Global Volcanism Program — volcano POI layer
  const [volcanoes] = await importSettled([import('./ops/volcanoes.js')])
  reg('volcanoes', volcanoes)

  // ACLED — armed conflict location & event data (B6)
  const [acled] = await importSettled([import('./ops/acled.js')])
  reg('acled', acled)

  // NOAA Climate Data — ocean currents + El Niño
  const [noaaClimate] = await importSettled([import('./ops/noaa-climate.js')])
  reg('noaa_climate', noaaClimate)

  // Joshua Project — Ethnic & People Groups (~17K point locations)
  const [joshuaProject] = await importSettled([import('./ops/joshua-project.js')])
  reg('joshua_project', joshuaProject)

  // World Time Zones — diverging choropleth on UTC offset, DST toggle, time Q&A
  const [timezones] = await importSettled([import('./ops/timezones.js')])
  reg('timezones', timezones)

  // Phase 3 operations — composition ops
  const [overlayCities] = await importSettled([import('./ops/overlay-cities.js')])
  reg('overlay_cities', overlayCities)

  // Spatial analysis operations — deterministic SQL builders (see
  // docs/SPATIAL-ANALYSIS-PLAYBOOK.md). LLM extracts entities; SQL is built
  // client-side and delegated to duckdb_query for unified rendering.
  const [
    center,
    proximity,
    similarity,
    suitability,
    neighbors,
    centrography,
    moran,
    pointPattern,
    hotspots,
    deviationalEllipse,
    changeMap,
    lisa,
    gravity,
    typology,
    gwr,
    proximityJoin,
    pointInRegion,
    bufferZone,
    clipToRegion,
    convexHull,
    voronoi,
    centroidOp,
    dissolveOp,
    unionOp,
    eraseOp,
    intersectOp,
  ] = await importSettled([
    import('./ops/center.js'),
    import('./ops/proximity.js'),
    import('./ops/similarity.js'),
    import('./ops/suitability.js'),
    import('./ops/neighbors.js'),
    import('./ops/centrography.js'),
    import('./ops/moran.js'),
    import('./ops/point-pattern.js'),
    import('./ops/hotspots.js'),
    import('./ops/deviational-ellipse.js'),
    import('./ops/change-map.js'),
    import('./ops/lisa.js'),
    import('./ops/gravity.js'),
    import('./ops/typology.js'),
    import('./ops/gwr.js'),
    import('./ops/proximity-join.js'),
    import('./ops/point-in-region.js'),
    import('./ops/buffer-zone.js'),
    import('./ops/clip-to-region.js'),
    import('./ops/convex-hull.js'),
    import('./ops/voronoi.js'),
    import('./ops/centroid.js'),
    import('./ops/dissolve.js'),
    import('./ops/union-regions.js'),
    import('./ops/erase.js'),
    import('./ops/intersect.js'),
  ])
  reg('center', center)
  reg('proximity', proximity)
  reg('similarity', similarity)
  reg('suitability', suitability)
  reg('neighbors', neighbors)
  reg('centrography', centrography)
  reg('moran', moran)
  reg('point_pattern', pointPattern)
  reg('hotspots', hotspots)
  reg('deviational_ellipse', deviationalEllipse)
  reg('change_map', changeMap)
  reg('lisa', lisa)
  reg('gravity', gravity)
  reg('typology', typology)
  reg('gwr', gwr)
  reg('proximity_join', proximityJoin)
  reg('point_in_region', pointInRegion)
  reg('buffer_zone', bufferZone)
  reg('clip_to_region', clipToRegion)
  reg('convex_hull', convexHull)
  reg('voronoi', voronoi)
  reg('centroid', centroidOp)
  reg('dissolve', dissolveOp)
  reg('union', unionOp)
  reg('erase', eraseOp)
  reg('intersect', intersectOp)

  // Phase 5 — Raster operations (satellite imagery layers)
  const renderRaster = await import('./ops/render-raster.js')
  reg('render_raster', renderRaster)
  reg('remove_raster', renderRaster.remove_raster)
  reg('adjust_opacity', renderRaster.adjust_opacity)

  // Raster analytics — GeoLibre terrain tools (slope/aspect/hillshade) on an
  // ingested DEM COG. Distinct from render_raster (viz tiles) and the terrain
  // camera op. Parser exposure is a separate, parser-eval-gated follow-up.
  const [terrainAnalysis] = await importSettled([import('./ops/terrain-analysis.js')])
  reg('terrain_analysis', terrainAnalysis)

  // Future operations:
  // registry.register('normalize', normalize);
  // registry.register('aggregate', aggregate);
  // registry.register('rank', rank);
  // registry.register('render_points', renderPoints);
  // registry.register('render_h3', renderH3);

  // Phase 5 operations:
  // registry.register('temporal_compare', temporal);
  // registry.register('render_temporal', renderTemporal);

  console.log(
    `[OperationRegistry] Initialized with ${registry.list().length} operations: ${registry.list().join(', ')}`
  )
}
